import type { RouteRecordRaw, Router } from 'vue-router'
import { useAuth } from '@/lib/utils/useAuth'
import { isStandaloneEnv } from '@/lib/utils/env'
import { Permissions } from '@/lib/types'

/**
 * Authenticated routes. Mounted under the `default` layout
 * template (sidebar + header + footer).
 *
 * `meta.auth: true` — guard enforces login.
 * `meta.permissions: string[]` — guard enforces ALL listed perms.
 * `meta.roles: string[]` — guard enforces ANY listed role.
 *
 * Adding a CRUD surface for a new server resource:
 *   1. Drop a `views/<Resource>List.vue` (+ optional Editor.vue)
 *   2. Add a route entry here
 *   3. Add a `MenuItem` row in `uiStore.menuItems` (permission-gated)
 */
export const authRoutes = (_router: Router): RouteRecordRaw[] => [
  {
    path: 'dashboard',
    name: 'Dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { auth: true, template: 'default' },
  },
  {
    // `/profile` → Settings → Profile section. Kept as a redirect
    // so any bookmark / external link to the old standalone profile
    // page lands on the right place. `name: 'Profile'` preserved as
    // the redirect's name so legacy `router.push({ name: 'Profile' })`
    // callers continue to work — they end up at the same destination
    // as the UserMenu dropdown.
    path: 'profile',
    name: 'Profile',
    redirect: { name: 'Configs', query: { section: 'profile' } },
    meta: { auth: true, template: 'default' },
  },
  {
    // Canonical "Kiosks" section route. KiosksView is the section
    // shell — renders Kiosks list flat for non-admins; wraps in tabs
    // (Kiosks / Locations / Templates) for actors holding the
    // catalog-management permissions. `?tab=locations` / `?tab=templates`
    // deep-links into the respective surface when the actor has access.
    //
    // Create / edit lives in the bottom drawer (no sub-route) — see
    // `KioskEditorBody` mounted by `KioskListView`.
    path: 'kiosks',
    name: 'KioskList',
    component: () => import('@/views/kiosks/KiosksView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_KIOSKS_VIEW] },
  },
  {
    // Mission Control — live operational monitoring + per-bay control
    // for one selected kiosk. Bay-detail management moved here from the
    // kiosk editor; a 10s poll refreshes bay state while a kiosk is
    // selected (paused during unsaved bay edits).
    path: 'mission-control',
    name: 'MissionControl',
    component: () => import('@/views/missioncontrol/MissionControlView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_KIOSKS_VIEW] },
  },
  {
    // OTA app-update dispatch — pick a release + target kiosks/locations.
    path: 'ota-updates',
    name: 'OtaUpdates',
    component: () => import('@/views/OtaUpdatesView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_OTA_MANAGE] },
  },
  {
    // Canonical "Devices" section route. The view internally switches
    // between desktop (inventory + slide-over drawer for models) and
    // mobile (tabs: Inventory | Models) based on viewport.
    // `?tab=models` deep-links to the Models surface in both modes.
    path: 'devices',
    name: 'Devices',
    component: () => import('@/views/devices/DevicesView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_DEVICES_VIEW] },
  },
  {
    // Legacy aliases — both old top-level entries now redirect into
    // the unified `/devices` route. `DeviceList` lands on default
    // (inventory) tab; `DeviceModelList` carries `?tab=models`.
    path: 'devices-legacy',
    name: 'DeviceList',
    redirect: { name: 'Devices' },
    meta: { auth: true, template: 'default' },
  },
  {
    path: 'device-models',
    name: 'DeviceModelList',
    redirect: { name: 'Devices', query: { tab: 'models' } },
    meta: { auth: true, template: 'default' },
  },
  {
    // Canonical "Users" section — UsersView is the shell, hosts
    // Users / Groups / Guests as permission-aware tabs. The
    // `?tab=groups` / `?tab=guests` query deep-links into the
    // respective surface.
    path: 'users',
    name: 'UserList',
    component: () => import('@/views/users/UsersView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_USERS_VIEW] },
  },
  {
    // Legacy redirect — `/groups` now folds into the Users
    // section's Groups tab. Keep the redirect so any saved
    // bookmarks / external links don't 404.
    path: 'groups',
    name: 'GroupList',
    redirect: { name: 'UserList', query: { tab: 'groups' } },
    meta: { auth: true, template: 'default' },
  },
  {
    // Path + route name kept stable (sidebar deep-links + saved bookmarks
    // continue to work after the Bay Access → Access Matrix rename).
    // Permission token also unchanged — see `Permissions::TENANT_BAY_ACCESS_VIEW`
    // (operator-facing label is now "Access Matrix"; wire token is internal).
    path: 'access-matrix',
    name: 'AccessMatrix',
    component: () => import('@/views/access-matrix/AccessMatrixView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_BAY_ACCESS_VIEW] },
  },
  {
    // Tracker — unified historical trace (replaces OrderList +
    // ActivityLog read surfaces). Polymorphic timeline anchored on
    // any entity (order / device / bay / kiosk / user). Cross-jump
    // chips on each node re-anchor the trace. Empty state (no
    // anchor) is the tenant-wide chronological stream.
    path: 'tracker',
    name: 'Tracker',
    component: () => import('@/views/tracker/TrackerView.vue'),
    meta: {
      auth: true,
      template: 'default',
      permissions: [Permissions.TENANT_TRACKER_VIEW],
    },
  },
  {
    // Per-order deep link — anchor Tracker on the specific order.
    // Drops in for prior `/orders/:id` bookmarks + notification
    // action-link payloads. Bare `/orders` is intentionally NOT
    // redirected — Tracker is anchor-required by design, so a
    // bare orders URL has no meaningful target (browse-orders
    // belongs on the future Reports surface).
    path: 'orders/:id',
    redirect: (to) => ({
      name: 'Tracker',
      query: { anchor_kind: 'order', anchor_id: String(to.params.id) },
    }),
  },
  {
    // Notifications — operator-facing alert configuration + inbox.
    // Placeholder today.
    path: 'notifications',
    name: 'Notifications',
    component: () => import('@/views/NotificationsView.vue'),
    meta: { auth: true, template: 'default' },
  },
  // On-Prem Clients — the appliances this cloud manages: their tokens, server updates and
  // health. Platform-only: gated on `PLATFORM_APPLIANCES_MANAGE` (in superAdminGrants());
  // AppliancePolicy denies every other actor. Absent from an appliance's own build (direct
  // URLs fall to the catch-all); the nav entry in uiStore.ts is gated on the same flag.
  ...(isStandaloneEnv
    ? []
    : [
        {
          path: 'onprem-clients',
          name: 'OnPremClients',
          component: () => import('@/views/appliances/AppliancesView.vue'),
          meta: {
            auth: true,
            template: 'default',
            permissions: [Permissions.PLATFORM_APPLIANCES_MANAGE],
          },
        },
        {
          // MDM Profiles — the MDM enrollments a flash station can set a unit up for.
          // Platform-only: gated on `PLATFORM_MDM_PROFILES_MANAGE` (in superAdminGrants());
          // AssemblyMdmProfilePolicy denies every other actor. Absent from an appliance's
          // own build like the entry above: an appliance runs no assembly line.
          path: 'mdm-profiles',
          name: 'MdmProfiles',
          component: () => import('@/views/mdm-profiles/MdmProfilesView.vue'),
          meta: {
            auth: true,
            template: 'default',
            permissions: [Permissions.PLATFORM_MDM_PROFILES_MANAGE],
          },
        },
      ]),
  {
    // Tenants — super_admin catalog + provisioning ("Create tenant",
    // "Resend invite"). Platform-only. Gated on the permission, not the
    // role name (same rule as SystemLogs): `PLATFORM_TENANTS_MANAGE` is
    // in superAdminGrants(), and TenantPolicy denies every other actor.
    path: 'tenants',
    name: 'Tenants',
    component: () => import('@/views/tenants/TenantsView.vue'),
    meta: {
      auth: true,
      template: 'default',
      permissions: [Permissions.PLATFORM_TENANTS_MANAGE],
    },
  },
  {
    // System logs — engineering-tier log surface. Platform-only —
    // tenant_admin / managers / users don't see this; only
    // super_admin / system. Placeholder today.
    path: 'system-logs',
    name: 'SystemLogs',
    component: () => import('@/views/SystemLogsView.vue'),
    meta: {
      auth: true,
      template: 'default',
      // Engineering-tier surface — only platform-tier actors see it.
      // `PLATFORM_VIEW_ALL` is the canonical permission marker; role
      // names stay out of route metadata so future role changes don't
      // ripple through guards.
      permissions: [Permissions.PLATFORM_VIEW_ALL],
    },
  },
  {
    // Reports — Activity log. It reads the Tracker's trace + stats
    // endpoints (tenant_admin-and-above), so it carries the same gate.
    path: 'reports',
    name: 'Reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_TRACKER_VIEW] },
  },
  {
    // Sync log — every sync to the tenant's lockers (paginated, per-locker
    // receipts in the drawer). Not role-gated: every operator of the tenant
    // reads it, like the sync pane.
    path: 'sync-log',
    name: 'SyncLog',
    component: () => import('@/views/reports/SyncLogView.vue'),
    meta: { auth: true, template: 'default' },
  },
  {
    // Device Status Report — supervisor department/location view of devices
    // not returned / damaged / not plugged in (filterable table + charts).
    path: 'device-status',
    name: 'DeviceStatusReport',
    component: () => import('@/views/DeviceStatusReportView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_DEVICES_VIEW] },
  },
  {
    // Asset Accounting — one route hosts five panes (Overview · Loss &
    // damage · Inventory value · Repair tracker · Thresholds & rules) as
    // top sub-tabs; the sidebar group's sub-items deep-link via `?view=`.
    // Gated on the device view permission, the same grant every
    // `/api/asset-accounting/*` read requires (AssetAccountingController).
    path: 'asset-accounting',
    name: 'AssetAccounting',
    component: () => import('@/views/asset-accounting/AssetAccountingView.vue'),
    meta: { auth: true, template: 'default', permissions: [Permissions.TENANT_DEVICES_VIEW] },
  },
  {
    // Configs — tenant-wide fleet config. Placeholder.
    path: 'configs',
    name: 'Configs',
    component: () => import('@/views/ConfigsView.vue'),
    meta: { auth: true, template: 'default' },
  },
  {
    path: 'logout',
    name: 'Logout',
    component: { template: '<div />' }, // never renders — beforeEnter handles it
    meta: { auth: true, template: 'default' },
    beforeEnter: async (_to, from, next) => {
      try {
        await useAuth().attemptLogout()
        next({ name: 'Login' })
      } catch {
        next(from)
      }
    },
  },
]
